import { Box, CircularProgress } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ChooseOrganizationPage } from '@/features/organizations/ChooseOrganizationPage';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Gate for authenticated routes.
 *
 * This is a UX control, not a security control — the API re-checks
 * authentication and membership on every request. Hiding a route here only
 * spares the user a pointless round trip.
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading, activeOrganization } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Authenticated but no organisation selected. Login deliberately does not
  // guess for a user who belongs to several, so ask — rather than rendering an
  // app with no data and no permissions, which looks broken.
  if (activeOrganization === null) {
    return <ChooseOrganizationPage />;
  }

  return <Outlet />;
}

/** Inverse gate: keeps signed-in users off the login and register pages. */
export function GuestRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <Outlet />;
}
